import React, { createContext, useContext, useState, useCallback } from 'react';
import type { Cart, CartItem } from '@runninglime/shared';
import { apiGet, apiPost, apiPut, apiDelete } from '../lib/api.ts';

interface CartContextType {
  cart: Cart | null;
  loading: boolean;
  fetchCart: () => Promise<void>;
  addItem: (productId: string, variantIndex: number, quantity: number) => Promise<{ success: boolean; error?: string }>;
  updateItem: (idx: number, quantity: number) => Promise<{ success: boolean; error?: string }>;
  removeItem: (idx: number) => Promise<{ success: boolean; error?: string }>;
  clearCart: () => Promise<void>;
  /** Number of distinct line items (unique product+variant combos) in the cart */
  itemCount: number;
  total: number;
  /** Incremented every time the cart changes — used to trigger badge animations */
  cartVersion: number;
}

const CartContext = createContext<CartContextType | null>(null);

export function CartProvider({ children }: { children: React.ReactNode }) {
  const [cart, setCart] = useState<Cart | null>(null);
  const [loading, setLoading] = useState(false);
  const [cartVersion, setCartVersion] = useState(0);

  const bump = () => setCartVersion((v) => v + 1);

  const fetchCart = useCallback(async () => {
    setLoading(true);
    const res = await apiGet<Cart>('/cart');
    if (res.success && res.data) {
      setCart(res.data);
    }
    setLoading(false);
  }, []);

  const addItem = useCallback(async (productId: string, variantIndex: number, quantity: number) => {
    const res = await apiPost<Cart>('/cart/items', { productId, variantIndex, quantity });
    if (res.success && res.data) {
      setCart(res.data);
      bump();
      return { success: true };
    }
    return { success: false, error: res.error };
  }, []);

  const updateItem = useCallback(async (idx: number, quantity: number) => {
    const res = await apiPut<Cart>(`/cart/items/${idx}`, { quantity });
    if (res.success && res.data) {
      setCart(res.data);
      bump();
      return { success: true };
    }
    return { success: false, error: res.error };
  }, []);

  const removeItem = useCallback(async (idx: number) => {
    const res = await apiDelete<Cart>(`/cart/items/${idx}`);
    if (res.success && res.data) {
      setCart(res.data);
      bump();
      return { success: true };
    }
    return { success: false, error: res.error };
  }, []);

  const clearCart = useCallback(async () => {
    const res = await apiDelete<Cart>('/cart');
    if (res.success && res.data) {
      setCart(res.data);
      bump();
    }
  }, []);

  // Count of distinct line items (unique product+variant combos)
  const itemCount = cart?.items.length ?? 0;
  const total = cart?.items.reduce((sum: number, item: CartItem) => sum + item.pricePerUnit * item.quantity, 0) ?? 0;

  return (
    <CartContext.Provider value={{ cart, loading, fetchCart, addItem, updateItem, removeItem, clearCart, itemCount, total, cartVersion }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within a CartProvider');
  return ctx;
}
